import React, { useMemo, useRef, useState } from 'react'

import { Button, Card } from '../ui'
import { bookingPath, enterBooking } from '../../lib/booking-entry'
import { image } from '../../lib/image'
import { addOnSectionsFrom } from '../../lib/service-catalogue'
import { usePublishedCatalogue } from '../../lib/use-published-catalogue'
import { LABELS } from '../../pages/services/extras/catalogue'

import './before-after.css'

// BEFORE AND AFTER, three real jobs, each one a drag-to-compare frame with the
// service that did it and a "Book this" link that lands on the booking screen
// with that service already ticked. Used in two places: the home page's
// recent-work section and the top of /services' add-ons. Read
// client/gwmc/DESIGN.md before changing it.
//
// WHY A SLIDER. A pair side by side at 400px is two 170px thumbnails, and bird
// droppings on a black bonnet are invisible at that size. The slider gives the
// one photograph the full width and lets the visitor do the comparing, which
// is the moment the result lands. No library: two <img>s, a clip-path driven
// by one custom property, pointer events for touch and mouse, and a native
// <input type="range"> laid over the frame for the keyboard and screen
// readers. `touch-action: pan-y` keeps a vertical swipe scrolling the page.
//
// No text on the photographs (DESIGN.md §3): "Before" and "After" sit in a
// row under the frame, and the alt text of each half says which it is.
//
// ON A PHONE ONE PAIR AT A TIME. Three stacked frames is about 1,200px of a
// phone page, so below 960px a row of three chips picks the pair and CSS
// hides the other two. Their images are `loading="lazy"` and `display: none`,
// so they are not fetched until chosen. From 960px all three show, 3-up, and
// the chips are hidden.
//
// NAMES AND PRICES ARE THE SERVER'S (§8.10, §9). The add-on rows come off
// /api/v2/services through the store (the page's own fetch owner asks; this
// reads with fetchIfMissing: false), labelled with the same LABELS the
// /services add-on list uses. Every add-on goes on any wash, so no pair names
// a base wash and Book this leaves the customer's wash (or the default) alone.
// A pair whose add-on the catalogue no longer sells is dropped once the
// catalogue has answered. Nothing in here types an amount.
const PAIRS = [
  {
    id: 'bird-droppings',
    files: 'audi-a5-bird-droppings',
    chip: 'Bird droppings',
    extra: 'bird_droppings_removal',
    name: 'Bird droppings removal',
    before: 'Before: a black Audi A5 bonnet, windscreen and headlight spattered with bird droppings, parked on a London street',
    after: 'After: the same Audi bonnet clean and waxed to a mirror gloss, reflecting the sky and the buildings opposite'
  },
  {
    id: 'engine-bay',
    files: 'engine-bay',
    chip: 'Engine bay',
    extra: 'engine_bay_cleaned',
    name: 'Engine bay cleaned',
    before: 'Before: a dusty engine bay with dead leaves piled up beside the bonnet strut',
    after: 'After: the same engine bay cleaned and dressed, the covers and plastics an even black'
  },
  {
    id: 'soft-top',
    files: 'soft-top',
    chip: 'Soft top',
    extra: 'soft_top_deep_clean',
    name: 'Soft roof deep clean and reproofing',
    before: 'Before: a black fabric convertible roof patched green with moss',
    after: 'After: the same fabric roof cleaned to an even black'
  }
]

const clamp = value => Math.min(100, Math.max(0, value))

// The add-ons this block sells, by catalogue key, as /services lists them.
const useAddOns = () => {
  const { entries, status } = usePublishedCatalogue({ fetchIfMissing: false })

  const byKey = useMemo(() => {
    const items = addOnSectionsFrom(entries, { labels: LABELS }).flatMap(group => group.items)
    return items.reduce((map, item) => ({ ...map, [item.key]: item }), {})
  }, [entries])

  return { byKey, ready: status === 'ready' }
}

const Slider = ({ pair, title }) => {
  const [pos, setPos] = useState(50)
  const frameRef = useRef(null)
  const dragRef = useRef(null)

  const moveTo = clientX => {
    const box = frameRef.current?.getBoundingClientRect()
    if (!box || box.width === 0) return
    setPos(clamp(Math.round(((clientX - box.left) / box.width) * 100)))
  }

  // A mouse moves the line as soon as it is pressed. A finger moves it only
  // once the gesture is clearly sideways, or on a tap, so that a vertical
  // swipe that starts on the photograph scrolls the page and leaves the line
  // where it was (the browser takes that gesture and sends pointercancel).
  const onPointerDown = event => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    dragRef.current = { x: event.clientX, mouse: event.pointerType === 'mouse', moved: false }
    if (dragRef.current.mouse) moveTo(event.clientX)
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }

  const onPointerMove = event => {
    const drag = dragRef.current
    if (!drag) return
    if (!drag.mouse && !drag.moved && Math.abs(event.clientX - drag.x) < 6) return
    drag.moved = true
    moveTo(event.clientX)
  }

  const onPointerUp = event => {
    const drag = dragRef.current
    if (drag && !drag.mouse && !drag.moved) moveTo(event.clientX)
    dragRef.current = null
  }

  const onPointerCancel = () => {
    dragRef.current = null
  }

  return (
    <div
      className="ba-frame"
      ref={frameRef}
      style={{ '--ba-pos': `${pos}%` }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}>
      <img
        className="ba-img"
        src={image(`./before-after-${pair.files}-after.webp`)}
        alt={pair.after}
        width={720}
        height={540}
        loading="lazy"
        decoding="async"
        draggable={false}
      />
      <img
        className="ba-img ba-img--before"
        src={image(`./before-after-${pair.files}-before.webp`)}
        alt={pair.before}
        width={720}
        height={540}
        loading="lazy"
        decoding="async"
        draggable={false}
      />
      <input
        className="ba-range"
        type="range"
        min={0}
        max={100}
        step={1}
        value={pos}
        onChange={event => setPos(clamp(Number(event.target.value)))}
        aria-label={`${title}, before and after. Move the slider to compare`}
        aria-valuetext={`${pos}% before, ${100 - pos}% after`}
      />
      <span className="ba-handle" aria-hidden="true">
        <span className="ba-knob" />
      </span>
    </div>
  )
}

const PairCard = ({ pair, addOn, active, source }) => {
  const title = addOn?.label || pair.name
  const detail = addOn ? `Add it to any wash for ${addOn.price}.` : null

  return (
    <Card as="article" className={`ba-card${active ? ' ba-card--active' : ''}`} id={`ba-${pair.id}`}>
      <Slider pair={pair} title={title} />

      <div className="ba-sides" aria-hidden="true">
        <span>Before</span>
        <span className="ba-hint">Drag to compare</span>
        <span>After</span>
      </div>

      <div className="ba-body">
        <h3 className="ba-title">{title}</h3>
        {/* One line reserved while the catalogue is in flight, so nothing
            under the card moves when the price lands. */}
        <p className="ba-detail">{detail}</p>
        <Button
          className="ba-book"
          variant="secondary"
          block
          href={bookingPath(null, { extra: pair.extra })}
          onClick={event => {
            event.preventDefault()
            enterBooking(null, source, { extra: pair.extra })
          }}>
          Book this<span className="gwmc-sr-only">: {title}</span>
        </Button>
      </div>
    </Card>
  )
}

// `source` names the placement on the PostHog booking_entry event:
// 'before_after_home' or 'before_after_services'.
export const BeforeAfter = ({ source }) => {
  const { byKey, ready } = useAddOns()
  const [chosen, setChosen] = useState(PAIRS[0].id)

  const pairs = PAIRS.filter(pair => !ready || byKey[pair.extra])
  const activeId = pairs.some(pair => pair.id === chosen) ? chosen : pairs[0]?.id

  if (pairs.length === 0) return null

  return (
    <div className="ba">
      {pairs.length > 1 && (
        <div className="ba-chips" role="group" aria-label="Choose a before and after">
          {pairs.map(pair => (
            <button
              type="button"
              key={pair.id}
              className="ba-chip"
              aria-pressed={pair.id === activeId}
              aria-controls={`ba-${pair.id}`}
              onClick={() => setChosen(pair.id)}>
              {pair.chip}
            </button>
          ))}
        </div>
      )}

      <div className="ba-grid">
        {pairs.map(pair => (
          <PairCard
            key={pair.id}
            pair={pair}
            addOn={byKey[pair.extra]}
            active={pair.id === activeId}
            source={source}
          />
        ))}
      </div>
    </div>
  )
}
